import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { call, put, SagaReturnType, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { ReportUserPayload } from 'src/models/report/ReportPayload';
import { RootState } from '.';
import api from '../api/BlancoClient';
import { extractInvalidChars } from '../lib/message-validator';
import { FriendItem } from '../models/FriendItem';
import { isFriendOnline } from '../models/FriendPresence';
import { FriendProfile } from '../models/FriendProfile';
import { UserProfile } from '../models/UserProfile';
import { actions as userActions, getFreshToken } from './user';
import { checkErrorType, Progress } from './utils/helper';

export type BestFriendErrorType = 'loading-failed' | 'unstable-network' | 'unknown';
export type BestFriendProfileErrorType = 'unstable-network' | 'unknown';
export type BestFriendDeleteErrorType = 'no-exist' | 'unstable-network' | 'unknown';
export type BestFriendReportErrorType = 'invalid-char' | 'unstable-network' | 'unknown';

interface BestFriendReportErrorPayload {
  invalidChars?: string[];
}

export interface BestFriendsState {
  friendItems: FriendItem[];
  profiles: FriendProfile[];
  isFetching: boolean;
  isRefreshing: boolean;
  deleteStatus?: Progress;
  reportStatus?: Progress;
  errorType?: BestFriendErrorType;
  profileErrorType?: BestFriendProfileErrorType;
  deleteErrorType?: BestFriendDeleteErrorType;
  reportErrorType?: BestFriendReportErrorType;
  reportErrorPayload?: BestFriendReportErrorPayload;
}

const initialState: BestFriendsState = {
  friendItems: [],
  profiles: [],
  isFetching: true,
  isRefreshing: false
};

const slice = createSlice({
  name: 'bestFriends',
  initialState,
  reducers: {
    getFriendList: state => {
      state.isFetching = true;
      state.deleteStatus = undefined;
      state.errorType = undefined;
    },

    getFriendListSucceeded: (state, { payload }: PayloadAction<FriendItem[]>) => {
      state.friendItems = payload;
      state.isFetching = false;
    },

    getFriendListFailed: (state, { payload }: PayloadAction<BestFriendErrorType>) => {
      state.isFetching = false;
      state.errorType = payload;
    },

    updateFriendList: (state, { payload }: PayloadAction<FriendItem[]>) => {
      state.friendItems = payload;
    },

    updateFriendPresences: state => {
      state.isRefreshing = true;
      state.errorType = undefined;
    },

    updateFriendPresencesSucceeded: state => {
      state.isRefreshing = false;
    },

    updateFriendPresencesFailed: (state, { payload }: PayloadAction<BestFriendErrorType>) => {
      state.isRefreshing = false;
      state.errorType = payload;
    },

    getFriendProfile: (state, _: PayloadAction<{ id: string; language: string }>) => {
      state.profileErrorType = undefined;
    },

    getFriendProfileSucceeded: (state, { payload }: PayloadAction<FriendProfile>) => {
      const index = state.profiles.findIndex(p => p.userId === payload.userId);
      if (index === -1) {
        state.profiles.push(payload);
      } else {
        state.profiles[index] = payload;
      }
    },

    getFriendProfileFailed: (state, { payload }: PayloadAction<BestFriendProfileErrorType>) => {
      state.profileErrorType = payload;
    },

    deleteFriend: (state, _: PayloadAction<{ id: string }>) => {
      state.deleteStatus = 'loading';
      state.deleteErrorType = undefined;
    },

    deleteFriendSucceeded: state => {
      state.deleteStatus = 'succeeded';
    },

    deleteFriendFailed: (state, { payload }: PayloadAction<BestFriendDeleteErrorType>) => {
      state.deleteStatus = 'failed';
      state.deleteErrorType = payload;
    },

    resetDeleteStatus: state => {
      state.deleteStatus = undefined;
      state.deleteErrorType = undefined;
    },

    reportFriend: (state, _: PayloadAction<ReportUserPayload>) => {
      state.reportStatus = 'loading';
      state.reportErrorType = undefined;
    },

    reportFriendSucceeded: state => {
      state.reportStatus = 'succeeded';
    },

    reportFriendFailed: (
      state,
      {
        payload
      }: PayloadAction<{
        errorType: BestFriendReportErrorType;
        errorPayload?: BestFriendReportErrorPayload;
      }>
    ) => {
      state.reportStatus = 'failed';
      state.reportErrorType = payload.errorType;
      state.reportErrorPayload = payload.errorPayload;
    },

    resetReportStatus: state => {
      state.reportStatus = undefined;
      state.reportErrorType = undefined;
      state.reportErrorPayload = undefined;
    },

    reset: state => {
      state.friendItems = initialState.friendItems;
      state.profiles = initialState.profiles;
      state.isFetching = initialState.isFetching;
      state.isRefreshing = initialState.isRefreshing;
      state.deleteStatus = undefined;
      state.reportStatus = undefined;
      state.errorType = undefined;
      state.profileErrorType = undefined;
      state.deleteErrorType = undefined;
      state.reportErrorType = undefined;
      state.reportErrorPayload = undefined;
    }
  }
});

export default slice.reducer;

const {
  getFriendList,
  updateFriendPresences,
  getFriendProfile,
  deleteFriend,
  resetDeleteStatus,
  reportFriend,
  resetReportStatus,
  reset,
  ...internalActions
} = slice.actions;

export const actions = {
  getFriendList,
  updateFriendPresences,
  getFriendProfile,
  deleteFriend,
  resetDeleteStatus,
  reportFriend,
  resetReportStatus,
  reset
};

const {
  getFriendListSucceeded,
  getFriendListFailed,
  updateFriendPresencesSucceeded,
  updateFriendPresencesFailed,
  deleteFriendSucceeded,
  deleteFriendFailed,
  updateFriendList,
  getFriendProfileSucceeded,
  getFriendProfileFailed,
  reportFriendSucceeded,
  reportFriendFailed
} = internalActions;

// Saga

export function* bestFriendsSaga() {
  yield takeEvery(getFriendList.type, getFriendListSaga);
  yield takeLatest(updateFriendPresences.type, updateFriendPresencesSaga);
  yield takeEvery(deleteFriend.type, deleteFriendSaga);
  yield takeLatest(getFriendProfile.type, getFriendProfileSaga);
  yield takeEvery(reportFriend.type, reportFriendSaga);
}

function* getFriendListSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const friendItems: SagaReturnType<typeof getFriendItems> = yield call(getFriendItems, baid);
    yield put(getFriendListSucceeded(friendItems));
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getFriendListFailed('unstable-network'));
    } else {
      yield put(getFriendListFailed('loading-failed'));
    }
  }
}

function* updateFriendPresencesSaga() {
  try {
    const baid: string = yield call(getFreshToken);
    const friendItems: SagaReturnType<typeof getFriendItems> = yield call(getFriendItems, baid);
    yield put(updateFriendList(friendItems));
    yield put(updateFriendPresencesSucceeded());
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(updateFriendPresencesFailed('unstable-network'));
    } else {
      yield put(updateFriendPresencesFailed('loading-failed'));
    }
  }
}

function* deleteFriendSaga(action: { type: typeof deleteFriend.type; payload: { id: string } }) {
  const bestFriendsState: BestFriendsState = yield select((state: RootState) => state.bestFriends);
  try {
    const baid: string = yield call(getFreshToken);
    yield call(api.deleteFriend, baid, action.payload.id);
    yield put(deleteFriendSucceeded());
    const filtered = bestFriendsState.friendItems.filter(item => item.id !== action.payload.id);
    yield put(updateFriendList(filtered));
  } catch (error) {
    console.error(error);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(deleteFriendFailed('unstable-network'));
    } else {
      let deleteErrorType: BestFriendDeleteErrorType;
      if (error.response.data?.code === '3005') {
        // 既にそのベストフレンドが削除されていて存在しなかった場合
        deleteErrorType = 'no-exist';
      } else {
        deleteErrorType = 'unknown';
      }
      yield put(deleteFriendFailed(deleteErrorType));
      if (deleteErrorType === 'no-exist') {
        const filtered = bestFriendsState.friendItems.filter(item => item.id !== action.payload.id);
        yield put(updateFriendList(filtered));
      }
    }
  }
}

function* getFriendProfileSaga(action: {
  type: typeof getFriendProfile.type;
  payload: { id: string; language: string };
}) {
  const { id, language } = action.payload;
  try {
    const baid: string = yield call(getFreshToken);
    const profile: UserProfile = yield call(api.getUserProfile, id, language, baid);
    yield put(getFriendProfileSucceeded({ ...profile, userId: id }));
  } catch (error) {
    const errorType = checkErrorType(error);
    switch (errorType) {
      case 'server':
        yield put(userActions.causeServerError(error.response.data?.code));
        break;
      case 'unstable-network':
        yield put(getFriendProfileFailed('unstable-network'));
        break;
      default:
        const bestFriendsState: BestFriendsState = yield select(
          (state: RootState) => state.bestFriends
        );
        const found = bestFriendsState.friendItems.find(item => item.id === id);
        if (found?.isReported && error.response.data?.code === '1101') {
          // 通報NGのユーザはプロフィールの取得が失敗してもエラーとしない。
        } else {
          yield put(getFriendProfileFailed('unknown'));
        }
    }
  }
}

function* reportFriendSaga(action: { type: typeof reportFriend.type; payload: ReportUserPayload }) {
  const reasonText = action.payload.reason;
  if (reasonText) {
    const invalidChars = extractInvalidChars(reasonText);
    if (invalidChars) {
      yield put(
        reportFriendFailed({
          errorType: 'invalid-char',
          errorPayload: { invalidChars }
        })
      );
      return;
    }
  }

  try {
    const baid: string = yield call(getFreshToken);
    const { type, language, category, reason, ugc } = action.payload;
    const { contentId, digest, createdAt } = ugc;
    yield call(
      api.reportSpam,
      type,
      category,
      reason,
      language,
      contentId,
      digest,
      createdAt,
      baid
    );
    yield put(reportFriendSucceeded());
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(reportFriendFailed({ errorType: 'unstable-network' }));
    } else {
      yield put(reportFriendFailed({ errorType: 'unknown' }));
    }
  } finally {
    yield put(resetReportStatus());
  }
}

async function getFriendItems(baid: string): Promise<FriendItem[]> {
  const [friendsResult, presencesResult] = await Promise.all([
    api.getFriends(baid),
    api.getFriendPresences(baid)
  ]);
  const { friends } = friendsResult;
  const { presences } = presencesResult;
  const friendItems = friends.map(friend => {
    const found = presences.find(p => p.userId === friend.userId);
    const isOnline = isFriendOnline(found?.state);
    const isReported = friend.screening === 'ng';
    const { userId, name, image, land } = friend;
    const landName = land.name;
    const landDisplayId = land.displayId;
    return {
      id: userId,
      name,
      landName,
      landDisplayId,
      image,
      isOnline,
      isReported
    };
  });
  return friendItems;
}
